import React, { useState, useEffect } from 'react';
import client from '../api/client';

const ENDO_LOCATIONS = [
  'Ovaries', 'Fallopian tubes', 'Uterosacral ligaments', 'Pouch of Douglas',
  'Bladder', 'Bowel', 'Rectum', 'Diaphragm', 'Appendix', 'Peritoneum',
];

const ORGANS_AFFECTED = [
  'Ovary (left)', 'Ovary (right)', 'Fallopian tube (left)', 'Fallopian tube (right)',
  'Bladder', 'Bowel', 'Appendix', 'Ureter', 'Diaphragm',
];

const INCISION_LOCATIONS = [
  'Umbilical (belly button)', 'Left lower quadrant', 'Right lower quadrant',
  'Suprapubic', 'Left upper quadrant', 'Right upper quadrant',
];

export default function SurgeryProfileForm({ profile, onSave }) {
  const [form, setForm] = useState({
    surgeryDate: '',
    surgeonName: '',
    endoStage: '',
    endoLocations: [],
    adhesions: false,
    organsAffected: [],
    procedureType: '',
    adhesiolysisPerformed: false,
    organWorkDetails: '',
    pathologyConfirmed: false,
    numIncisions: '',
    incisionLocations: [],
    surgeryNotes: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (profile) {
      setForm({
        surgeryDate: profile.surgeryDate || '',
        surgeonName: profile.surgeonName || '',
        endoStage: profile.endoStage || '',
        endoLocations: profile.endoLocations || [],
        adhesions: profile.adhesions || false,
        organsAffected: profile.organsAffected || [],
        procedureType: profile.procedureType || '',
        adhesiolysisPerformed: profile.adhesiolysisPerformed || false,
        organWorkDetails: profile.organWorkDetails || '',
        pathologyConfirmed: profile.pathologyConfirmed || false,
        numIncisions: profile.numIncisions || '',
        incisionLocations: profile.incisionLocations || [],
        surgeryNotes: profile.surgeryNotes || '',
      });
    }
  }, [profile]);

  const toggle = (field, value) => {
    const current = form[field] || [];
    if (current.includes(value)) {
      setForm({ ...form, [field]: current.filter(v => v !== value) });
    } else {
      setForm({ ...form, [field]: [...current, value] });
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        numIncisions: form.numIncisions ? parseInt(form.numIncisions) : null,
      };
      await client.put('/recovery/surgery-profile', payload);
      if (onSave) onSave();
    } catch {
      alert('Failed to save surgery profile.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-white/80 mb-1">Surgery Date</label>
          <input
            type="date"
            value={form.surgeryDate}
            onChange={e => setForm({ ...form, surgeryDate: e.target.value })}
            className="w-full rounded-lg bg-white/10 border-white/20 text-white px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-white/80 mb-1">Surgeon Name</label>
          <input
            type="text"
            value={form.surgeonName}
            onChange={e => setForm({ ...form, surgeonName: e.target.value })}
            placeholder="Dr. ..."
            className="w-full rounded-lg bg-white/10 border-white/20 text-white placeholder-white/40 px-3 py-2"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-white/80 mb-1">Endo Stage</label>
          <select
            value={form.endoStage}
            onChange={e => setForm({ ...form, endoStage: e.target.value })}
            className="w-full rounded-lg bg-white/10 border-white/20 text-white px-3 py-2"
          >
            <option value="">Select...</option>
            <option value="Stage I (Minimal)">Stage I (Minimal)</option>
            <option value="Stage II (Mild)">Stage II (Mild)</option>
            <option value="Stage III (Moderate)">Stage III (Moderate)</option>
            <option value="Stage IV (Severe)">Stage IV (Severe)</option>
            <option value="Unknown">Unknown</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-white/80 mb-1">Procedure Type</label>
          <select
            value={form.procedureType}
            onChange={e => setForm({ ...form, procedureType: e.target.value })}
            className="w-full rounded-lg bg-white/10 border-white/20 text-white px-3 py-2"
          >
            <option value="">Select...</option>
            <option value="Excision">Excision</option>
            <option value="Ablation">Ablation</option>
            <option value="Excision + Ablation">Excision + Ablation</option>
            <option value="Diagnostic only">Diagnostic only</option>
            <option value="Other">Other</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-white/80 mb-2">Endo Locations Found</label>
        <div className="flex flex-wrap gap-2">
          {ENDO_LOCATIONS.map(loc => (
            <button
              key={loc}
              type="button"
              onClick={() => toggle('endoLocations', loc)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition
                ${(form.endoLocations || []).includes(loc)
                  ? 'bg-white/20 text-white ring-2 ring-white/30'
                  : 'bg-white/10 text-white/60 hover:bg-white/20'
                }`}
            >
              {loc}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-4">
        <button
          type="button"
          onClick={() => setForm({ ...form, adhesions: !form.adhesions })}
          className={`px-4 py-2 rounded-xl text-sm font-medium transition
            ${form.adhesions ? 'bg-white/20 text-white ring-2 ring-white/30' : 'bg-white/10 text-white/60 hover:bg-white/20'}`}
        >
          Adhesions found
        </button>
        <button
          type="button"
          onClick={() => setForm({ ...form, adhesiolysisPerformed: !form.adhesiolysisPerformed })}
          className={`px-4 py-2 rounded-xl text-sm font-medium transition
            ${form.adhesiolysisPerformed ? 'bg-white/20 text-white ring-2 ring-white/30' : 'bg-white/10 text-white/60 hover:bg-white/20'}`}
        >
          Adhesiolysis performed
        </button>
        <button
          type="button"
          onClick={() => setForm({ ...form, pathologyConfirmed: !form.pathologyConfirmed })}
          className={`px-4 py-2 rounded-xl text-sm font-medium transition
            ${form.pathologyConfirmed ? 'bg-white/20 text-white ring-2 ring-white/30' : 'bg-white/10 text-white/60 hover:bg-white/20'}`}
        >
          Pathology confirmed
        </button>
      </div>

      <div>
        <label className="block text-sm font-medium text-white/80 mb-2">Organs Affected</label>
        <div className="flex flex-wrap gap-2">
          {ORGANS_AFFECTED.map(org => (
            <button
              key={org}
              type="button"
              onClick={() => toggle('organsAffected', org)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition
                ${(form.organsAffected || []).includes(org)
                  ? 'bg-white/20 text-white ring-2 ring-white/30'
                  : 'bg-white/10 text-white/60 hover:bg-white/20'
                }`}
            >
              {org}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-white/80 mb-1">Organ Work Details</label>
        <textarea
          value={form.organWorkDetails}
          onChange={e => setForm({ ...form, organWorkDetails: e.target.value })}
          placeholder="Describe any organ work performed..."
          rows={3}
          className="w-full rounded-lg bg-white/10 border-white/20 text-white placeholder-white/40 shadow-sm px-3 py-2"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-white/80 mb-1">Number of Incisions</label>
          <input
            type="number"
            min="0"
            max="10"
            value={form.numIncisions}
            onChange={e => setForm({ ...form, numIncisions: e.target.value })}
            className="w-full rounded-lg bg-white/10 border-white/20 text-white px-3 py-2"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-white/80 mb-2">Incision Locations</label>
        <div className="flex flex-wrap gap-2">
          {INCISION_LOCATIONS.map(loc => (
            <button
              key={loc}
              type="button"
              onClick={() => toggle('incisionLocations', loc)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition
                ${(form.incisionLocations || []).includes(loc)
                  ? 'bg-white/20 text-white ring-2 ring-white/30'
                  : 'bg-white/10 text-white/60 hover:bg-white/20'
                }`}
            >
              {loc}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-white/80 mb-1">Surgery Notes</label>
        <textarea
          value={form.surgeryNotes}
          onChange={e => setForm({ ...form, surgeryNotes: e.target.value })}
          placeholder="Any additional notes about the surgery..."
          rows={4}
          className="w-full rounded-lg bg-white/10 border-white/20 text-white placeholder-white/40 shadow-sm px-3 py-2"
        />
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={saving}
          className="px-6 py-3 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700 disabled:opacity-50 transition"
        >
          {saving ? 'Saving...' : profile ? 'Update Profile' : 'Save Profile'}
        </button>
      </div>
    </div>
  );
}
